import { useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '../types/events';
import { MessageBubble } from './MessageBubble';
import { StreamingMessage } from './StreamingMessage';

export interface MessageListProps {
  /** Array of messages to display */
  messages: Message[];
  /** Background color for user message bubbles */
  userMessageBgColor: string;
  /** Text color inside user message bubbles */
  userMessageTextColor?: string;
  /** Background color for bot message bubbles */
  botMessageBgColor?: string;
  /** Background color for message area */
  backgroundColor?: string;
  /** Text color for empty states and inherited copy */
  textColor?: string;
  /** Empty state message (optional) */
  emptyMessage?: string;
  /** Empty state sub-message (optional) */
  emptySubMessage?: string;
  /** Confirmation summaries for answered tool UIs, keyed by tool_call_id */
  answeredSummaries?: Record<string, string>;
  /**
   * The one answered prompt whose confirmation stays tappable, if any: the location question,
   * which a visitor may correct. Every other answer is final once given.
   */
  reopenableToolCallId?: string | null;
  /** Open the bottom sheet for a given tool UI message */
  onOpenToolSheet?: (toolCallId: string) => void;
  /** Avatar URL shown next to bot messages */
  avatarUrl?: string;
  /** Conversation starter texts */
  conversationStarters?: string[];
  /** Handler for when a conversation starter is clicked */
  onStarterClick?: (text: string) => void;
  /** Whether to show assistant typing indicator */
  isAssistantTyping?: boolean;
  /**
   * When true, re-scroll to the bottom as the software keyboard opens/closes
   * (the visual viewport resizes). Enabled in the fullscreen mobile layout so
   * the latest message isn't left hidden behind the input above the keyboard.
   */
  trackKeyboard?: boolean;
}

/**
 * Scrollable message list component with auto-scroll to bottom.
 *
 * Features:
 * - Flex-col layout with gap-3 spacing between messages
 * - Auto-scrolls to bottom when new messages arrive
 * - Displays empty state when no messages exist
 * - Renders messages using MessageBubble component
 */
export function MessageList({
  messages,
  userMessageBgColor,
  userMessageTextColor = '#ffffff',
  botMessageBgColor = '#f3f4f6',
  backgroundColor = '#f9fafb',
  textColor = '#6b7280',
  emptyMessage,
  emptySubMessage,
  answeredSummaries,
  reopenableToolCallId,
  onOpenToolSheet,
  avatarUrl,
  conversationStarters,
  onStarterClick,
  isAssistantTyping = false,
  trackKeyboard = false,
}: MessageListProps) {
  const { t } = useTranslation('widget');
  const resolvedEmptyMessage = emptyMessage ?? t('messages.noMessagesYet', { ns: 'common' });
  const resolvedEmptySubMessage = emptySubMessage;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change. Scroll this list's OWN
  // overflow container directly rather than calling `scrollIntoView` on a
  // sentinel — `scrollIntoView` scrolls every scrollable ancestor, including
  // ChatWindow's body wrapper, which holds the absolutely-positioned bottom
  // sheet. A nonzero scroll offset on that wrapper drags the (closed,
  // translated-away) sheet back into view, so a dismissed picker/form reappears
  // on the next message (CAIV-458). Scrolling only this container keeps the
  // ancestor at scrollTop 0.
  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior });
    } else {
      // jsdom / environments without Element.scrollTo
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages, scrollToBottom]);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!trackKeyboard || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => scrollToBottom('auto'));
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackKeyboard, scrollToBottom]);

  // Compute grouping: determine first/last in consecutive sender group
  const groupedMessages = useMemo(() => {
    return messages.map((message, index) => {
      const prev = index > 0 ? messages[index - 1] : null;
      const next = index < messages.length - 1 ? messages[index + 1] : null;
      const sameSenderAsPrev = prev?.senderType === message.senderType;
      const sameSenderAsNext = next?.senderType === message.senderType;
      return {
        message,
        isFirstInGroup: !sameSenderAsPrev,
        isLastInGroup: !sameSenderAsNext,
        showAvatar: !sameSenderAsNext, // avatar on last message in group
      };
    });
  }, [messages]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto p-4 bg-gray-50"
      style={{ backgroundColor, overscrollBehavior: 'contain' }}
    >
      <div className="flex flex-col">
        {messages.length === 0 ? (
          <div className="text-center text-sm mt-8" style={{ color: textColor }}>
            <p>{resolvedEmptyMessage}</p>
            {resolvedEmptySubMessage && <p className="mt-2">{resolvedEmptySubMessage}</p>}
            {conversationStarters && conversationStarters.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                {conversationStarters.slice(0, 2).map((text) => (
                  <button
                    key={text}
                    onClick={() => onStarterClick?.(text)}
                    type="button"
                    style={{
                      background: 'transparent',
                      border: '1px solid #cbd5e1',
                      borderRadius: '9999px',
                      padding: '6px 14px',
                      fontSize: '13px',
                      color: textColor,
                      cursor: 'pointer',
                      lineHeight: '1.4',
                      fontFamily: 'inherit',
                      transition: 'background-color 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)';
                      e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                  >
                    {text}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          groupedMessages.map(({ message, isFirstInGroup, isLastInGroup, showAvatar }) => {
            const toolCallId = message.metadata?.tool_ui?.tool_call_id;
            const summary = toolCallId ? answeredSummaries?.[toolCallId] : undefined;
            const canReopen = !!toolCallId && toolCallId === reopenableToolCallId;
            return (
              <div
                key={message.id}
                style={{ marginTop: isFirstInGroup ? '12px' : '2px' }}
              >
                <MessageBubble
                  message={message}
                  userMessageBgColor={userMessageBgColor}
                  userMessageTextColor={userMessageTextColor}
                  botMessageBgColor={botMessageBgColor}
                  botMessageTextColor={textColor}
                  avatarUrl={avatarUrl}
                  showAvatar={showAvatar}
                  isFirstInGroup={isFirstInGroup}
                  isLastInGroup={isLastInGroup}
                  toolUiAnswered={summary !== undefined}
                  toolUiSummary={summary}
                  toolUiCanReopen={canReopen}
                  onOpenToolSheet={toolCallId ? () => onOpenToolSheet?.(toolCallId) : undefined}
                />
              </div>
            );
          })
        )}
        {isAssistantTyping && (
          <div style={{ marginTop: '12px' }}>
            <StreamingMessage
              content=""
              isStreaming
              backgroundColor={botMessageBgColor}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageList;
